/**
 * Verification Service
 * 
 * Service for handling email verification flows in the frontend.
 * Provides methods for verifying emails, checking verification status,
 * and managing the verification process.
 */

import axios from 'axios';
import config from '../config.js';
import { errorHandler, ErrorType, ErrorSeverity } from '../utils/errorHandler.js';

// Create axios instance for verification API
const verificationApi = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.timeouts.verification || config.defaultTimeout,
  headers: {
    'Content-Type': 'application/json',
    ...config.api.headers
  }
});

// Request interceptor to add auth token
verificationApi.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
verificationApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Handle errors with structured error handler
    const structuredError = errorHandler.handleError(error, {
      service: 'verification',
      endpoint: error.config?.url,
      method: error.config?.method
    });
    
    return Promise.reject(structuredError);
  }
);

/**
 * Verification Service Class
 */
class VerificationService {
  constructor() {
    this.baseUrl = '/verify';
    this.authBaseUrl = '/auth';
  }

  /**
   * Verify email with token
   * @param {string} token - Verification token from email
   * @returns {Promise<Object>} Verification result
   */
  async verifyEmail(token) {
    try {
      // URL encode the token to handle special characters
      const encodedToken = encodeURIComponent(token);
      console.log(`[DEBUG] VerificationService.verifyEmail: Calling /api/auth/verify-email/${encodedToken}`);
      const response = await verificationApi.get(`${this.authBaseUrl}/verify-email/${encodedToken}`);
      
      console.log(`[DEBUG] VerificationService.verifyEmail: Response received:`, response);
      
      return {
        success: true,
        message: response.message || 'Email verified successfully',
        user: response.user,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.verifyEmail: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: error.type === ErrorType.AUTHENTICATION ? 'VERIFICATION_FAILED' : 'UNKNOWN_ERROR'
      };
    }
  }

  /**
   * Complete verification and generate authentication tokens
   * @returns {Promise<Object>} Verification completion result
   */
  async completeVerification() {
    try {
      console.log(`[DEBUG] VerificationService.completeVerification: Calling /api/verify/complete`);
      const response = await verificationApi.post(`${this.baseUrl}/complete`);
      
      console.log(`[DEBUG] VerificationService.completeVerification: Response received:`, response);
      
      return {
        success: true,
        tokens: response.tokens,
        message: response.message || 'Email verification completed successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.completeVerification: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: error.type === ErrorType.AUTHENTICATION ? 'COMPLETION_FAILED' : 'UNKNOWN_ERROR'
      };
    }
  }

  /**
   * Resend verification email
   * @param {string} email - Email address to resend verification to
   * @returns {Promise<Object>} Resend result
   */
  async resendVerificationEmail(email) {
    try {
      console.log(`[DEBUG] VerificationService.resendVerificationEmail: Calling /api/verify/resend for email: ${email}`);
      const response = await verificationApi.post(`${this.baseUrl}/resend`, { email });
      
      console.log(`[DEBUG] VerificationService.resendVerificationEmail: Response received:`, response);
      
      return {
        success: true,
        message: response.message || 'Verification email sent successfully',
        nextResendAvailable: response.nextResendAvailable,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.resendVerificationEmail: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: error.type === ErrorType.AUTHENTICATION ? 'RESEND_FAILED' : 'UNKNOWN_ERROR',
        nextResendAvailable: error.details?.nextResendAvailable
      };
    }
  }

  /**
   * Check verification status for current user
   * @param {string} userId - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object>} Verification status
   */
  async getVerificationStatus(userId = null) {
    try {
      const endpoint = userId 
        ? `${this.baseUrl}/status/${userId}`
        : `${this.baseUrl}/status`;
      
      const response = await verificationApi.get(endpoint);
      
      return {
        success: true,
        status: response,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'STATUS_CHECK_FAILED'
      };
    }
  }

  /**
   * Check email verification status by email
   * @param {string} email - Email address to check
   * @returns {Promise<Object>} Email verification status
   */
  async checkEmailVerificationStatus(email) {
    try {
      console.log(`[DEBUG] VerificationService.checkEmailVerificationStatus: Calling /api/verify/check-email/${email}`);
      const response = await verificationApi.get(`${this.baseUrl}/check-email/${email}`);
      
      console.log(`[DEBUG] VerificationService.checkEmailVerificationStatus: Response received:`, response);
      
      return {
        success: true,
        status: response,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.checkEmailVerificationStatus: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'EMAIL_CHECK_FAILED'
      };
    }
  }

  /**
   * Validate verification token without completing verification
   * @param {string} token - Verification token to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateVerificationToken(token) {
    try {
      console.log(`[DEBUG] VerificationService.validateVerificationToken: Calling /api/verify/validate-token with token`);
      const response = await verificationApi.post(`${this.baseUrl}/validate-token`, { token });
      
      console.log(`[DEBUG] VerificationService.validateVerificationToken: Response received:`, response);
      
      return {
        success: true,
        valid: response.data?.valid || false,
        message: response.data?.message || 'Token validation completed',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.validateVerificationToken: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'VALIDATION_FAILED',
        valid: false
      };
    }
  }

  /**
   * Get verification statistics (admin function)
   * @returns {Promise<Object>} Verification statistics
   */
  async getVerificationStatistics() {
    try {
      console.log(`[DEBUG] VerificationService.getVerificationStatistics: Calling /api/verify/stats`);
      const response = await verificationApi.get(`${this.baseUrl}/stats`);
      
      console.log(`[DEBUG] VerificationService.getVerificationStatistics: Response received:`, response);
      
      return {
        success: true,
        statistics: response,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.getVerificationStatistics: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'STATISTICS_FAILED'
      };
    }
  }

  /**
   * Process verification from URL (for email links)
   * @param {string} token - Verification token from URL
   * @returns {Promise<Object>} Verification process result
   */
  async processVerificationFromUrl(token) {
    try {
      // First validate the token
      const validationResult = await this.validateVerificationToken(token);
      
      if (!validationResult.success || !validationResult.valid) {
        return {
          success: false,
          error: validationResult.error || 'Invalid verification token',
          code: 'INVALID_TOKEN',
          requiresNewToken: true
        };
      }
      
      // If valid, complete the verification
      const verificationResult = await this.verifyEmail(token);
      
      if (verificationResult.success) {
        return {
          success: true,
          message: verificationResult.message,
          user: verificationResult.user,
          autoLogin: true,
          timestamp: new Date().toISOString()
        };
      } else {
        return {
          success: false,
          error: verificationResult.error,
          code: verificationResult.code || 'VERIFICATION_FAILED',
          requiresNewToken: verificationResult.code === 'VERIFICATION_FAILED'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'PROCESS_FAILED',
        requiresNewToken: true
      };
    }
  }

  /**
   * Verify email with 6-digit code
   * @param {string} code - 6-digit verification code
   * @param {string} email - User email address
   * @returns {Promise<Object>} Verification result with tokens
   */
  async verifyCode(code, email) {
    try {
      console.log(`[DEBUG] VerificationService.verifyCode: Calling /api/verify/code`);
      const response = await verificationApi.post(`${this.baseUrl}/code`, {
        code,
        email
      });
      
      console.log(`[DEBUG] VerificationService.verifyCode: Response received:`, response);
      
      return {
        success: true,
        message: response.message || 'Email verified successfully',
        tokens: response.tokens,
        user: response.user,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.verifyCode: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: error.type === ErrorType.AUTHENTICATION ? 'VERIFICATION_FAILED' : 'UNKNOWN_ERROR',
        attemptsRemaining: error.response?.data?.attemptsRemaining,
        expired: error.response?.data?.expired,
        newCodeSent: error.response?.data?.newCodeSent
      };
    }
  }

  /**
   * Resend verification code
   * @param {string} email - User email address
   * @returns {Promise<Object>} Resend result
   */
  async resendVerificationCode(email) {
    try {
      console.log(`[DEBUG] VerificationService.resendVerificationCode: Calling /api/verify/resend-code for email: ${email}`);
      const response = await verificationApi.post(`${this.baseUrl}/resend-code`, { email });
      
      console.log(`[DEBUG] VerificationService.resendVerificationCode: Response received:`, response);
      
      return {
        success: true,
        message: response.message || 'Verification code sent successfully',
        codeExpiresAt: response.codeExpiresAt,
        nextResendAvailable: response.nextResendAvailable,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[DEBUG] VerificationService.resendVerificationCode: Error:`, error);
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: error.type === ErrorType.AUTHENTICATION ? 'RESEND_FAILED' : 'UNKNOWN_ERROR',
        nextResendAvailable: error.response?.data?.nextResendAvailable
      };
    }
  }

  /**
   * Check if user needs verification
   * @param {Object} user - User object
   * @returns {boolean} True if user needs verification
   */
  userNeedsVerification(user) {
    if (!user) return false;
    
    // Check if email is verified
    if (user.emailVerified) return false;
    
    // Check if user has a verification token (means verification is pending)
    if (user.verificationToken) return true;
    
    // Default to needing verification if email is not verified
    return !user.emailVerified;
  }

  /**
   * Get time until next resend is available
   * @param {string} nextResendAvailable - ISO timestamp of next available resend
   * @returns {Object} Time until next resend
   */
  getTimeUntilNextResend(nextResendAvailable) {
    if (!nextResendAvailable) {
      return {
        available: true,
        seconds: 0,
        minutes: 0,
        formatted: 'Now'
      };
    }
    
    const now = new Date();
    const nextResend = new Date(nextResendAvailable);
    const diffMs = nextResend - now;
    
    if (diffMs <= 0) {
      return {
        available: true,
        seconds: 0,
        minutes: 0,
        formatted: 'Now'
      };
    }
    
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    
    return {
      available: false,
      seconds,
      minutes,
      formatted: minutes > 0 
        ? `${minutes} minute${minutes !== 1 ? 's' : ''}` 
        : `${seconds} second${seconds !== 1 ? 's' : ''}`
    };
  }

  /**
   * Create verification redirect URL
   * @param {string} token - Verification token
   * @param {string} redirectTo - URL to redirect after verification
   * @returns {string} Full verification URL
   */
  createVerificationUrl(token, redirectTo = null) {
    const baseUrl = window.location.origin;
    let url = `${baseUrl}/verify/${token}`;
    
    if (redirectTo) {
      url += `?redirect=${encodeURIComponent(redirectTo)}`;
    }
    
    return url;
  }

  /**
   * Extract token from URL
   * @param {string} url - URL to extract token from
   * @returns {string|null} Verification token or null
   */
  extractTokenFromUrl(url) {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Look for verify/[token] pattern
      const verifyIndex = pathParts.indexOf('verify');
      if (verifyIndex !== -1 && verifyIndex + 1 < pathParts.length) {
        return pathParts[verifyIndex + 1];
      }
      
      // Check query parameters
      const tokenParam = urlObj.searchParams.get('token');
      if (tokenParam) {
        return tokenParam;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to extract token from URL:', error);
      return null;
    }
  }

  /**
   * Handle verification callback (for OAuth flows)
   * @param {string} provider - OAuth provider
   * @param {Object} params - URL parameters
   * @returns {Promise<Object>} Verification callback result
   */
  async handleVerificationCallback(provider, params) {
    try {
      const response = await verificationApi.post(`${this.baseUrl}/callback/${provider}`, params);
      
      return {
        success: true,
        message: response.message || 'Verification callback processed',
        user: response.user,
        tokens: response.tokens,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler.createUserMessage(error),
        details: error.details,
        code: 'CALLBACK_FAILED'
      };
    }
  }

  /**
   * Send welcome email after verification
   * @param {string} email - User email
   * @param {Object} userData - User data for personalization
   * @returns {Promise<Object>} Welcome email result
   */
  async sendWelcomeEmail(email, userData = {}) {
    try {
      const response = await verificationApi.post(`${this.baseUrl}/welcome`, {
        email,
        userData
      });
      
      return {
        success: true,
        message: response.message || 'Welcome email sent successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // Don't fail verification if welcome email fails
      console.warn('Welcome email failed:', error);
      return {
        success: false,
        error: 'Welcome email could not be sent',
        code: 'WELCOME_EMAIL_FAILED',
        nonCritical: true
      };
    }
  }

  /**
   * Get queue position for verification email
   * @param {string} email - User email
   * @returns {Promise<Object>} Queue position info
   */
  async getQueuePosition(email) {
    try {
      const response = await verificationApi.get(`${this.baseUrl}/queue-position`, {
        params: { email }
      });
      
      return {
        success: true,
        queued: response.queued || false,
        position: response.position || null,
        estimatedWaitTime: response.estimatedWaitTime || null,
        estimatedWaitMinutes: response.estimatedWaitMinutes || null,
        queuedAt: response.queuedAt || null
      };
    } catch (error) {
      console.warn('Queue position check failed:', error);
      return {
        success: false,
        queued: false,
        error: errorHandler.createUserMessage(error)
      };
    }
  }
}

// Create singleton instance
const verificationService = new VerificationService();

// Export singleton
export default verificationService;

// Export class for testing/mocking
export { VerificationService };
